import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { getIndustry } from '@shared/catalog'
import type { QuoteInput } from '@shared/types'
import { ApiError, api } from '../lib/api'
import { Banner, Button, Card, TextField } from '../ui'
import { PricingConfigurator, QuoteSummary } from '../components/PricingConfigurator'
import { CheckoutPanel, InvoiceHistory } from '../components/CheckoutPanel'
import { PlanChangeNotice } from '../components/PlanChangeNotice'
import { Inbox } from '../components/Inbox'
import { Contacts } from '../components/Contacts'
import { Tasks } from '../components/Tasks'
import { Notifications } from '../components/Notifications'
import { AgentCredentials } from '../components/AgentCredentials'
import { TenantSetup } from '../components/TenantSetup'
import { TenantGuard } from '../components/TenantGuard'
import { PairNumber } from '../components/PairNumber'
import { DisclosureNotice, DisclosureReminder, useDisclosure } from '../components/Disclosure'
import { Team } from '../components/Team'
import { CadenceShell } from '../components/CadenceShell'
import { Broadcasts } from '../components/Broadcasts'
import { MetricsDashboard } from '../components/MetricsDashboard'
import { Settings } from '../components/Settings'
import { Integrations } from '../components/Integrations'
import { Workflows } from '../components/Workflows'
import { AutomationRules } from '../components/AutomationRules'
import { FlowBuilder } from '../components/FlowBuilder'
import { Products } from '../components/Products'
import { Groups } from '../components/Groups'
import { Exports } from '../components/Exports'
import { Knowledge } from '../components/Knowledge'
import { AutoReply } from '../components/AutoReply'
import { ScheduledMessages } from '../components/ScheduledMessages'
import { AuditLog } from '../components/AuditLog'
import { SsoSettings } from '../components/SsoSettings'

type Tab =
  | 'overview'
  | 'inbox'
  | 'contacts'
  | 'tasks'
  | 'notifications'
  | 'broadcasts'
  | 'scheduled-messages'
  | 'products'
  | 'numbers'
  | 'team'
  | 'agents'
  | 'groups'
  | 'auto-reply'
  | 'knowledge'
  | 'workflows'
  | 'automation'
  | 'flows'
  | 'exports'
  | 'audit-log'
  | 'sso'
  | 'integrations'
  | 'settings'
  | 'billing'

const TABS: Tab[] = [
  'overview', 'inbox', 'contacts', 'tasks', 'notifications', 'broadcasts', 'scheduled-messages', 'products',
  'numbers', 'team', 'agents', 'groups', 'auto-reply', 'knowledge', 'workflows', 'automation', 'flows',
  'exports', 'audit-log', 'sso', 'integrations', 'settings', 'billing',
]

/** The hash is the one thing a full reload keeps -- so it, not local state, is the source of
 *  truth for which screen is open. A bookmark or a reload lands back on the same tab instead of
 *  always dropping to Overview, and #inbox is now a real, sharable address for the screen this
 *  team lives in all day. */
function readTabFromHash(): Tab | null {
  const raw = window.location.hash.slice(1)
  return (TABS as string[]).includes(raw) ? (raw as Tab) : null
}

export function AppDashboard() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { checkout?: string }
  // Stripe hands the payer back here after checkout; land them on billing so the
  // result is visible instead of buried behind a tab they have to find.
  const [tab, setTab] = useState<Tab>(() => (search.checkout ? 'billing' : readTabFromHash() ?? 'overview'))
  const [routedToDatabaseSetup, setRoutedToDatabaseSetup] = useState(false)

  // Keep the hash in sync with whichever tab is actually open, without pushing a history entry
  // per click -- replaceState updates the address bar (and what a reload will restore) while
  // leaving the back button free for real navigation, not a click-by-click undo of the sidebar.
  useEffect(() => {
    if (window.location.hash.slice(1) !== tab) {
      window.history.replaceState(null, '', `#${tab}`)
    }
  }, [tab])

  // The counterpart: a hand-edited URL, a bookmark, or the back/forward buttons change the hash
  // without going through setTab, so the tab has to follow the hash back.
  useEffect(() => {
    const onHashChange = () => {
      const next = readTabFromHash()
      if (next) setTab(next)
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const session = useQuery({ queryKey: ['session'], queryFn: api.session })
  const workspace = useQuery({
    queryKey: ['workspace'],
    queryFn: api.workspace,
    enabled: !!session.data?.user,
  })
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: api.tenant, enabled: !!session.data?.user })
  const taskBadge = useQuery({
    queryKey: ['tasks', 'open', ''],
    queryFn: () => api.tasks({ status: 'open' }),
    enabled: !!session.data?.user,
    refetchInterval: 60_000,
  })
  const notificationBadge = useQuery({
    queryKey: ['notifications', 'unread-badge'],
    queryFn: () => api.notifications(true),
    enabled: !!session.data?.user,
    refetchInterval: 60_000,
  })
  const workspaces = useQuery({
    queryKey: ['workspaces'],
    queryFn: api.workspaces,
    enabled: !!session.data?.user,
  })
  const switchWorkspace = useMutation({
    mutationFn: api.switchWorkspace,
    // A workspace switch changes the answer to nearly every query in the app at once — reloading
    // is the one operation guaranteed to leave nothing serving the old workspace's cached data.
    onSuccess: () => window.location.reload(),
  })

  useEffect(() => {
    if (session.isError) {
      navigate({ to: '/login' })
      return
    }
    if (!session.data) return
    if (!session.data.user) navigate({ to: '/login' })
    else if (!session.data.workspace?.onboardingCompletedAt) navigate({ to: '/onboarding' })
  }, [session.data, session.isError, navigate])

  // A trial with no database yet has nothing to show on any other tab, so send it straight to
  // the one screen that can fix that (Inbox, where TenantSetup lives) instead of a dead end on
  // Overview. Only once: after that the nav is visible and browsing elsewhere is a real choice.
  useEffect(() => {
    if (routedToDatabaseSetup || search.checkout || !tenant.data) return
    setRoutedToDatabaseSetup(true)
    if (tenant.data.tenant?.status !== 'ready') setTab('inbox')
  }, [tenant.data, routedToDatabaseSetup, search.checkout])

  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => navigate({ to: '/' }),
  })

  if (workspace.isPending) {
    return (
      <div className="container section stack gap-4">
        <div className="skeleton" style={{ height: '40px', width: '260px' }} />
        <div className="skeleton" style={{ height: '240px' }} />
      </div>
    )
  }

  /**
   * The same bug `AgentCredentials` already carries a note about, in the one place where it
   * takes the whole product down rather than one panel: this was `isPending || !workspace.data`,
   * with no error branch anywhere in the file. Once `/api/workspace` failed and the single retry
   * was spent, the app shell stayed as two grey rectangles forever — no message, no retry, no
   * way out but a manual reload. Sign in while the endpoint is 5xx and that is the entire
   * product.
   */
  if (!workspace.data) {
    const message =
      workspace.error instanceof ApiError || workspace.error instanceof Error
        ? workspace.error.message
        : 'Your workspace could not be loaded.'
    return (
      <div className="container section stack gap-4" style={{ maxWidth: '48ch' }}>
        <h1 className="cad-title">Wami could not load your workspace</h1>
        <p className="cad-muted">{message}</p>
        <div className="row gap-2 wrap">
          <button type="button" className="cad-btn" onClick={() => void workspace.refetch()}>
            Try again
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={() => logout.mutate()}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  const data = workspace.data

  const numbers = data.numbers
  const connected = numbers.filter((n) => n.status === 'connected').length
  // A number that was connected and has since dropped is the case worth surfacing: replies
  // simply stop arriving, with nothing on screen to say why.
  const degraded = numbers.some(
    (n) => n.sessionStatus === 'logged_out' || n.sessionStatus === 'failed',
  )

  const industry = getIndustry(data.workspace!.industryId)!
  const canManageBilling = data.workspace!.role === 'owner' || data.workspace!.role === 'admin'

  return (
    <CadenceShell
      fillMain={tab === 'inbox'}
      workspaceName={data.workspace!.name}
      workspaceSubtitle={industry.name}
      userEmail={session.data?.user?.email}
      demoBanner={data.workspace!.isDemo}
      nav={[
        { items: [{ id: 'overview', label: 'Overview' }] },
        {
          heading: 'Conversations',
          items: [
            { id: 'inbox', label: 'Inbox' },
            { id: 'contacts', label: 'Contacts' },
            { id: 'tasks', label: 'Tasks' },
            { id: 'notifications', label: 'Notifications' },
          ],
        },
        {
          heading: 'Outbound',
          items: [
            { id: 'broadcasts', label: 'Bulk messages' },
            { id: 'scheduled-messages', label: 'Scheduled messages' },
            { id: 'products', label: 'Catalogue' },
          ],
        },
        {
          heading: 'Channels & team',
          items: [
            { id: 'numbers', label: 'WhatsApp numbers' },
            { id: 'team', label: 'Team' },
            { id: 'agents', label: 'AI agents' },
            { id: 'groups', label: 'Groups' },
          ],
        },
        {
          heading: 'Automation',
          items: [
            { id: 'auto-reply', label: 'Auto replies' },
            { id: 'knowledge', label: 'Knowledge' },
            { id: 'workflows', label: 'Routing rules' },
            { id: 'automation', label: 'Automation rules' },
            { id: 'flows', label: 'Flows' },
          ],
        },
        {
          heading: 'Workspace',
          items: [
            { id: 'integrations', label: 'Integrations' },
            { id: 'exports', label: 'Exports' },
            { id: 'audit-log', label: 'Audit log' },
            { id: 'sso', label: 'SSO and SCIM' },
            { id: 'settings', label: 'Settings' },
            // Invoices and plan changes are an admin/owner concern — the API already withholds
            // that data from an agent or viewer, so the nav item that leads nowhere useful is
            // withheld too rather than opening onto mostly-empty cards.
            ...(canManageBilling ? [{ id: 'billing' as const, label: 'Plan and billing' }] : []),
          ],
        },
      ]}
      current={tab}
      onNavigate={setTab}
      phones={{ connected, total: numbers.length || data.subscription!.numbers, degraded }}
      onPhonesClick={() => setTab('numbers')}
      tasksDue={taskBadge.data?.counts}
      onTasksClick={() => setTab('tasks')}
      unreadCount={notificationBadge.data?.items.length}
      onNotificationsClick={() => setTab('notifications')}
      onSignOut={() => logout.mutate()}
      workspaceSwitcher={
        workspaces.data
          ? {
              workspaces: workspaces.data.workspaces.map((ws) => ({
                id: ws.id,
                name: ws.name,
                industryName: getIndustry(ws.industryId)?.name ?? ws.industryId,
                role: ws.role,
              })),
              activeWorkspaceId: workspaces.data.activeWorkspaceId,
              onSwitch: (workspaceId) => switchWorkspace.mutate(workspaceId),
              switching: switchWorkspace.isPending,
            }
          : undefined
      }
    >
      <AccessBanner access={data.access} billingEnabled={data.billingEnabled} onManage={() => setTab('billing')} />
      {tab === 'overview' && (
        <TenantGuard>
          <GettingStartedChecklist checklist={data.checklist} onNavigate={setTab} />
          <MetricsDashboard
            workspaceName={data.workspace!.name}
            industryName={industry.name}
            onGoToInbox={() => setTab('inbox')}
          />
        </TenantGuard>
      )}
      {tab === 'inbox' && (
        <div className="stack gap-4">
          <TenantSetup hideWhenReady />
          <Inbox industryName={industry.name} />
        </div>
      )}
      {tab === 'contacts' && (
        <TenantGuard>
          <Contacts />
        </TenantGuard>
      )}
      {tab === 'tasks' && (
        <TenantGuard>
          <Tasks />
        </TenantGuard>
      )}
      {tab === 'notifications' && (
        <TenantGuard>
          <Notifications />
        </TenantGuard>
      )}
      {tab === 'broadcasts' && (
        <TenantGuard>
          <Broadcasts />
        </TenantGuard>
      )}
      {tab === 'scheduled-messages' && (
        <TenantGuard>
          <ScheduledMessages />
        </TenantGuard>
      )}
      {tab === 'products' && (
        <TenantGuard>
          <Products />
        </TenantGuard>
      )}
      {tab === 'team' && <Team />}
      {tab === 'agents' && <AgentCredentials />}
      {/* Groups' own list/management routes need no tenant database for an unrestricted role
          (the one every brand-new trial owner has) — only its health rollup sub-panel does, and
          that already degrades silently on error rather than crashing. Not worth gating the
          whole screen for one already-safe sub-feature. */}
      {tab === 'groups' && <Groups />}
      {tab === 'auto-reply' && (
        <TenantGuard>
          <AutoReply />
        </TenantGuard>
      )}
      {tab === 'knowledge' && (
        <TenantGuard>
          <Knowledge />
        </TenantGuard>
      )}
      {tab === 'workflows' && (
        <TenantGuard>
          <Workflows />
        </TenantGuard>
      )}
      {tab === 'automation' && (
        <TenantGuard>
          <AutomationRules />
        </TenantGuard>
      )}
      {tab === 'flows' && (
        <TenantGuard>
          <FlowBuilder />
        </TenantGuard>
      )}
      {tab === 'exports' && (
        <TenantGuard>
          <Exports />
        </TenantGuard>
      )}
      {tab === 'audit-log' && (
        <TenantGuard>
          <AuditLog />
        </TenantGuard>
      )}
      {tab === 'sso' && <SsoSettings />}
      {tab === 'integrations' && (
        <TenantGuard>
          <Integrations />
        </TenantGuard>
      )}
      {tab === 'settings' && (
        <TenantGuard>
          <Settings />
        </TenantGuard>
      )}
      {tab === 'numbers' && <Numbers data={data} />}
      {tab === 'billing' && canManageBilling && <Billing data={data} checkoutResult={search.checkout} />}
    </CadenceShell>
  )
}

type Data = NonNullable<ReturnType<typeof useQuery<Awaited<ReturnType<typeof api.workspace>>>>['data']>

/**
 * A single line at the top of every screen when the subscription needs attention, with the one
 * action that fixes it. Silent while a subscription is healthy and the trial has real time left,
 * so it only ever appears when it is genuinely useful. This is the self-serve path: a trial that
 * is running out, ended, or a payment that failed each resolves from here without a support
 * ticket.
 */
function AccessBanner({
  access,
  billingEnabled,
  onManage,
}: {
  access: Data['access']
  billingEnabled: boolean
  onManage: () => void
}) {
  if (!access) return null

  // A healthy paid subscription, or a trial with more than a couple of days left, needs no nudge.
  if (access.reason === 'active') return null
  if (access.reason === 'trial' && (access.trialDaysLeft ?? 99) > 2) return null

  const copy: Record<string, { tone: 'info' | 'warn' | 'error'; message: string; cta: string }> = {
    trial: {
      tone: 'warn',
      message:
        access.trialDaysLeft === 0
          ? 'Your trial ends today.'
          : `${access.trialDaysLeft} day${access.trialDaysLeft === 1 ? '' : 's'} left in your trial.`,
      cta: 'Add a payment method',
    },
    trial_expired: { tone: 'error', message: 'Your trial has ended. Add a payment method to keep going.', cta: 'Upgrade' },
    past_due: { tone: 'error', message: 'Your last payment failed. Update your billing to restore paid features.', cta: 'Fix billing' },
    canceled: { tone: 'info', message: 'Your subscription is cancelled. Reactivate any time.', cta: 'Reactivate' },
    inactive: { tone: 'info', message: 'This workspace has no active plan.', cta: 'Choose a plan' },
  }
  let c = copy[access.reason] ?? copy.inactive

  // Every message above but the running countdown tells the reader to go add or fix a payment
  // method right now — true only once a card processor is actually wired up for this workspace.
  // Without that, the billing screen this banner links to can only say "billing opens soon"
  // (CheckoutPanel.tsx), so the banner and the screen it points to were flatly contradicting each
  // other. Told straight instead, in the same calm tone as that screen, rather than manufacturing
  // urgency nothing can resolve. The plain countdown is left alone — "N days left" stays true
  // regardless of whether billing is live yet.
  if (!billingEnabled && access.reason !== 'trial') {
    c = {
      tone: 'info',
      message: "Billing isn't live for this workspace yet, so nothing is blocked — you'll be able to manage your plan as soon as it's ready.",
      cta: 'View plan',
    }
  }

  return (
    <Banner tone={c.tone}>
      <span className="row between wrap gap-3" style={{ width: '100%', alignItems: 'center' }}>
        <span>{c.message}</span>
        <Button onClick={onManage}>{c.cta}</Button>
      </span>
    </Banner>
  )
}

const CHECKLIST_DISMISSED_KEY = 'cad-getting-started-dismissed'

/**
 * The three things a genuinely new workspace hasn't done yet — a connected number, a second
 * person, and one real reply sent — surfaced together right where a first-time owner lands,
 * rather than left to discover the same three screens on their own over the first week.
 *
 * Disappears for good once all three are done; a manual dismiss (for "I'll do this later")
 * is remembered locally only, same seam `CadenceShell`'s own rail-collapse state uses — this
 * is a per-browser nicety, not data worth a round trip to persist server-side.
 */
function GettingStartedChecklist({
  checklist,
  onNavigate,
}: {
  checklist: Data['checklist']
  onNavigate: (tab: Tab) => void
}) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CHECKLIST_DISMISSED_KEY) === 'true'
    } catch {
      return false
    }
  })

  const items: { key: string; done: boolean; label: string; detail: string; cta: string; tab: Tab }[] = [
    {
      key: 'numberConnected',
      done: checklist.numberConnected,
      label: 'Connect a WhatsApp number',
      detail: 'Scan a QR code from the phone that owns the number.',
      cta: 'Connect a number',
      tab: 'numbers',
    },
    {
      key: 'teamInvited',
      done: checklist.teamInvited,
      label: 'Invite your team',
      detail: 'Bring in whoever else will be answering chats.',
      cta: 'Invite people',
      tab: 'team',
    },
    {
      key: 'firstReplySent',
      done: checklist.firstReplySent,
      label: 'Send your first reply',
      detail: 'Open a conversation and reply — try a quick reply shortcut to see how those work.',
      cta: 'Open inbox',
      tab: 'inbox',
    },
  ]

  if (dismissed || items.every((item) => item.done)) return null

  const dismiss = () => {
    try {
      localStorage.setItem(CHECKLIST_DISMISSED_KEY, 'true')
    } catch {
      // Private-mode: still dismisses for the session, just doesn't survive a reload.
    }
    setDismissed(true)
  }

  return (
    <Card variant="outlined">
      <div className="stack gap-3">
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h2 className="cad-section-title">Getting started</h2>
          <button type="button" className="cad-btn" data-variant="text" onClick={dismiss}>
            Dismiss
          </button>
        </div>
        <div className="stack gap-2">
          {items.map((item) => (
            <div
              key={item.key}
              className="row between wrap gap-3"
              style={{ alignItems: 'center', opacity: item.done ? 0.6 : 1 }}
            >
              <span className="row gap-3" style={{ alignItems: 'center' }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: item.done ? '#fff' : 'var(--cad-ink-muted)',
                    background: item.done ? 'var(--cad-good)' : 'transparent',
                    border: item.done ? 'none' : '1.5px solid var(--cad-line)',
                  }}
                >
                  {item.done ? '✓' : ''}
                </span>
                <span className="stack" style={{ gap: '1px' }}>
                  <span style={{ textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{item.detail}</span>
                </span>
              </span>
              {!item.done && (
                <Button onClick={() => onNavigate(item.tab)} variant="outlined">
                  {item.cta}
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}

function Numbers({ data }: { data: Data }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ label: '', phone: '' })
  const disclosure = useDisclosure()

  // "Is there a spare" is a question about numbers that are actually up, not about how many
  // rows exist — a second number that is unlinked is not somewhere a send can fail over to.
  const connectedCount = data.numbers.filter((n) => n.sessionStatus === 'connected').length

  const add = useMutation({
    mutationFn: () => api.addNumber(form),
    onSuccess: async () => {
      setForm({ label: '', phone: '' })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.removeNumber(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspace'] }),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    add.mutate()
  }

  const atCapacity = data.numbers.length >= data.subscription!.numbers

  // A QR rotates every twenty seconds or so, so the list refreshes while any number is
  // mid-pairing and stops once nothing is waiting. A history import outlives the QR by
  // minutes and has its own counter to move, so it keeps the same poll running.
  const pairing = data.numbers.some(
    (n) =>
      n.sessionStatus === 'awaiting_qr' ||
      n.sessionStatus === 'starting' ||
      n.historyState === 'importing',
  )
  useEffect(() => {
    if (!pairing) return
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['workspace'] })
    }, 5000)
    return () => clearInterval(timer)
  }, [pairing, queryClient])

  return (
    <div className="stack gap-6" style={{ maxWidth: '840px' }}>
      <div className="stack gap-2">
        <h1 className="headline-large">WhatsApp numbers</h1>
        <p className="body-large text-muted">
          Connect regular WhatsApp numbers. No Business API account and no template approval.
        </p>
        {/* The same sentence as the disclosure's summary, on the screen where a number is
            connected — so the trade is visible every time, not only the first time. */}
        {disclosure.data?.accepted && (
          <DisclosureReminder acceptedAt={disclosure.data.acceptedAt} />
        )}
      </div>

      {/* Before the numbers, not after them: the point of reading this is to decide which
          number to connect, which is a decision already made by the time you scroll past. */}
      {disclosure.isSuccess && !disclosure.data.accepted && <DisclosureNotice />}

      <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
        <h2 className="title-large">Connected</h2>
        <span className="body-medium text-muted tabular">
          {data.numbers.length} of {data.subscription!.numbers} used
        </span>
      </div>

      {data.numbers.length === 0 ? (
        <p className="body-medium text-muted">No numbers yet. Add your first one below.</p>
      ) : (
        <div className="stack gap-3">
          {data.numbers.map((number) => (
            <PairNumber
              key={number.id}
              number={number}
              pollingEnabled={pairing}
              hasBackup={connectedCount > 1}
              disclosureAccepted={disclosure.data?.accepted ?? false}
              onRemove={() => remove.mutate(number.id)}
              removing={remove.isPending && remove.variables === number.id}
            />
          ))}
        </div>
      )}

      <Card variant="outlined">
        <form className="stack gap-4" onSubmit={submit} noValidate>
          <h2 className="title-large">Add a number</h2>
          {add.error && <Banner tone="error">{(add.error as Error).message}</Banner>}
          {atCapacity && (
            <Banner tone="warn">
              Your plan covers {data.subscription!.numbers} numbers. Increase the count in billing to
              add more.
            </Banner>
          )}
          <TextField
            label="Label"
            placeholder="Support line"
            required
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <TextField
            label="Phone number"
            placeholder="+14155550123"
            required
            help="International format, including country code."
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Button type="submit" disabled={add.isPending || atCapacity}>
            {add.isPending ? 'Adding…' : 'Add number'}
          </Button>
        </form>
      </Card>
    </div>
  )
}

function Billing({ data, checkoutResult }: { data: Data; checkoutResult?: string }) {
  const queryClient = useQueryClient()
  const sub = data.subscription!
  const [config, setConfig] = useState<QuoteInput>({
    planId: sub.planId as QuoteInput['planId'],
    industryId: data.workspace!.industryId as QuoteInput['industryId'],
    localeId: data.workspace!.localeId as QuoteInput['localeId'],
    billingCycle: sub.billingCycle,
    seats: sub.seats,
    numbers: sub.numbers,
    conversations: sub.conversations,
    addons: sub.addons as QuoteInput['addons'],
  })

  const save = useMutation({
    // The preview's proration instant is sent back so the charge matches what was displayed.
    mutationFn: () => api.updateSubscription(config, preview.data?.preview.prorationDate),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspace'] }),
  })

  // Comparing lengths alone would miss a swap of one module for another.
  const sameAddons =
    config.addons.length === sub.addons.length &&
    config.addons.every((addon) => sub.addons.includes(addon))

  const dirty =
    config.planId !== sub.planId ||
    config.billingCycle !== sub.billingCycle ||
    config.seats !== sub.seats ||
    config.numbers !== sub.numbers ||
    config.conversations !== sub.conversations ||
    !sameAddons

  /**
   * Prices the pending change against the live subscription. Keyed on the configuration so a
   * further edit supersedes the in-flight request rather than showing a stale figure.
   */
  const preview = useQuery({
    queryKey: ['subscription-preview', config],
    queryFn: () => api.previewSubscriptionChange(config),
    enabled: dirty,
    staleTime: 30_000,
    retry: false,
  })

  return (
    <div className="stack gap-6">
      <div className="stack gap-2">
        <h1 className="headline-large">Plan and billing</h1>
        <p className="body-large text-muted" style={{ maxWidth: '60ch' }}>
          Adjust your plan at any time — we price the change before you commit. Your industry and
          region are fixed after onboarding, so contact support to change those.
        </p>
      </div>

      {checkoutResult === 'cancelled' && sub.paymentStatus !== 'paid' && (
        <Banner tone="info">Checkout was cancelled. Nothing was charged.</Banner>
      )}
      {checkoutResult === 'success' && sub.paymentStatus !== 'paid' && (
        <Banner tone="info">
          Payment submitted. We are waiting on confirmation from your bank — this page updates on its
          own once it clears.
        </Banner>
      )}

      <div
        className="pricing-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(300px, 1fr)',
          gap: '32px',
          alignItems: 'start',
        }}
      >
        <Card variant="outlined">
          <PricingConfigurator
            input={config}
            onChange={setConfig}
            showIndustry={false}
            showLocale={false}
          />
        </Card>

        <div className="stack gap-4" style={{ position: 'sticky', top: '88px' }}>
          <Card variant="filled">
            <QuoteSummary input={config} />
          </Card>
          <CheckoutPanel data={data} hasUnsavedChanges={dirty} />
          <InvoiceHistory data={data} />

          {dirty && (
            <PlanChangeNotice
              preview={preview.data?.preview ?? { timing: 'not_billed_yet' }}
              localeId={config.localeId}
              pending={preview.isFetching}
            />
          )}
          {save.isSuccess && !dirty && (
            <Banner tone="success">
              {save.data?.change.timing === 'next_cycle'
                ? `Saved. The new price starts on ${
                    save.data.change.effectiveAt
                      ? new Date(save.data.change.effectiveAt).toLocaleDateString()
                      : 'your next billing date'
                  }.`
                : save.data?.change.timing === 'immediate'
                  ? 'Saved. The change is live and prorated on your next invoice.'
                  : 'Subscription updated.'}
            </Banner>
          )}
          {save.error && <Banner tone="error">{(save.error as Error).message}</Banner>}
          <Button size="large" full disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : dirty ? 'Save changes' : 'No changes to save'}
          </Button>
          <Link to="/pricing" className="btn btn-text btn-full">
            Compare all plans
          </Link>
        </div>
      </div>
    </div>
  )
}
